
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useCharacterOperations = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const createCharacter = async (characterData: any) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("characters")
        .insert([characterData])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Character created successfully",
      });

      return data;
    } catch (error: any) {
      console.error("Error creating character:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create character",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCharacter = async (id: string, characterData: any) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("characters")
        .update({ ...characterData, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Character updated successfully",
      });

      return data;
    } catch (error: any) {
      console.error("Error updating character:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update character",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCharacter = async (id: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("characters")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Character deleted successfully",
      });
    } catch (error: any) {
      console.error("Error deleting character:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete character",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createCharacter,
    updateCharacter,
    deleteCharacter,
    isLoading,
  };
};
